import {useDropzone} from "react-dropzone";
import InputIconUpload from "../images/icons/input-bar-icons/icon-input-bar-upload.svg";
import {useEffect, useRef, useState} from "react";
import {useGenerateDispatch, useGenerateStatus} from "../context/GenerateContext";
import Cropper from "react-cropper";
import "react-cropper/node_modules/cropperjs/dist/cropper.css";

import Image from "image-js";

import NeutralGrey from '../images/neutral_grey_512.png';
import Modal from "./Modal";
import {getImageDescription} from "../api-helper";
import {useAccountContext} from "../context/AccountContext";
import cardCopy from "../images/icons/image-card-icons/card-icon-copy-image.svg";
import cardSave from "../images/icons/image-card-icons/card-icon-save-image.svg";
import cardPopulatePrompt from "../images/icons/image-card-icons/card-icon-populate-prompt-from-upload.svg";
import {downloadString} from "./CollectionImage"
import {usePromptContext} from "../context/PromptContext";
import { copyDescriptionToClipboard, downloadDescriptionAsPromptFile, populatePromptField } from './ImageDescriptionUtils';

export default function ImageUpload(props) {
    const {acceptedFiles, getRootProps, getInputProps, isDragActive} = useDropzone({maxFiles: 1});
    const [showCropper, setShowCropper] = useState(false);
    const [tempImage, setTempImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null)
    const [imageDescription, setImageDescription] = useState("");
    const generateStatus = useGenerateStatus();
    const cropperRef = useRef(null);
    const onCrop = () => {
        const cropper = cropperRef.current?.cropper;
        setCroppedImage(cropper.getCroppedCanvas().toDataURL());
    };
    const accountContext = useAccountContext();
    const [clipboardMessage, setClipboardMessage] = useState('');
    const { updatePrompt } = usePromptContext();
    const [downloadName, setDownloadName] = useState(`Promptus`)

    useEffect(() => {
        if (props.image === null) {
            acceptedFiles.splice(0, 1);
        }
    }, [props.image])


    async function fetchAndSetDescription(base64String) {
        setImageDescription("Generating ...")
        try {
            const modifiedBase64 = base64String.replace("data:image/jpeg;base64,", "");
            const imageDescription = await getImageDescription(accountContext.accountDetails.accountNo, "", modifiedBase64);
            console.log('Received image description:', imageDescription);
            if (imageDescription && imageDescription.image_description) {
                setImageDescription(imageDescription.image_description);
            } else {
                console.error('No description found in response:', imageDescription);
            }
        } catch (error) {
            console.error('Error fetching image description:', error);
        }
    }

    useEffect(() => {

        if (acceptedFiles.length > 0) {
            const reader = new FileReader();
            if (acceptedFiles[0].type.match('image')) {

                reader.onloadend = async () => {
                    const base64String = reader.result;
                    let image = await Image.load(base64String);

                    if (image.height > image.width) {
                        image = image.resize({height: 800, preserveAspectRatio: true});
                    } else {
                        image = image.resize({width: 800, preserveAspectRatio: true});
                    }

                    setTempImage(image.toBase64());
                    setShowCropper(true);
                    await fetchAndSetDescription(base64String);
                };
                reader.readAsDataURL(acceptedFiles[0]);
            } else if (acceptedFiles[0].path.includes('.prompt')){
                reader.onloadend = () => {
                    const fileContents = reader.result;
                    try {
                        console.log('File Contents: ',fileContents);
                        const jsonData = JSON.parse(fileContents);
                        console.log('Parsed Data: ',jsonData); // Here's your parsed JSON data
                        props.setPromptFile(JSON.stringify(jsonData));
                    } catch (error) {
                        console.error("Error parsing JSON:", error);
                    }
                };
                reader.readAsText(acceptedFiles[0]);

            }
        }
    }, [acceptedFiles])

    const doneCropping = async () => {
        let neutralImage = await Image.load(NeutralGrey);
        let image = await Image.load(croppedImage);
        image = image.resize({height: 512, preserveAspectRatio: true});
        image = neutralImage.rgba8().insert(image.rgba8());
        props.setImage(image.toBase64());
        setShowCropper(false);
        setCroppedImage(null);
    }

    if (showCropper && tempImage) {
        return (
            <Modal heading={"Crop Image"} closeEvent={() => {
                setShowCropper(false);
                setTempImage(null);
            }}>
                {croppedImage ?
                    <div
                        className={"flex flex-row gap-2 w-full flex-wrap justify-between p-2 items-center bg-white/50 text-dark-blue"}>
                        <div className={"rounded bg-dark-blue text-yellow px-4 py-2"}>
                            <button onClick={doneCropping}>Accept</button>
                        </div>
                    </div>
                    : null}
                <div className={"w-full h-auto"}>
                    <Cropper
                        src={`data:image/*;base64,${tempImage}`}
                        style={{height: "100%", width: "auto"}}
                        // Cropper.js options
                        background={true}
                        aspectRatio={1}
                        guides={false}
                        cropend={onCrop}
                        modal={false}
                        ref={cropperRef}
                        scalable={false}
                        autoCrop={true}
                    />
                </div>
                <div className="container text-center mx-auto" style={{maxWidth: '700px'}}>
                    {imageDescription}

                    <div className="mt-3">
                        <button className="me-3" title="Copy Description To Clipboard"
                                onClick={() => copyDescriptionToClipboard(imageDescription, setClipboardMessage)}>
                            <img src={cardCopy} alt={"Copy Description To Clipboard Icon"}/>
                        </button>
                        <button className="me-3" title="Save As Prompt"
                                onClick={() => downloadDescriptionAsPromptFile(imageDescription, downloadName)}>
                            <img src={cardSave} alt={"Save As Prompt Icon"}/>
                        </button>
                        <button className="me-3" title="Populate Prompt" onClick={() => populatePromptField(imageDescription, updatePrompt)}>
                            <img src={cardPopulatePrompt} alt={"Populate Prompt Icon"}/>
                        </button>
                        <span>{clipboardMessage}</span>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <div {...getRootProps()} className={`${generateStatus.loading ? 'pointer-events-none' : ''}`}>
            <input {...getInputProps()} />
            <div id="upload"
                 className={`w-14 relative h-full bg-gray-300 flex flex-row place-items-center justify-center overflow-hidden`}>
                {props.image ?
                    <>
                        {props.overlayImage ?
                            <img className={"absolute w-full h-full opacity-50 hover:opacity-0 transition"}
                                 src={`data:image/*;base64,${props.overlayImage}`} alt={""}/> : null}
                        <img src={`data:image/*;base64,${props.image}`}
                             className={`object-contain w-full h-full ${isDragActive ? 'blur-md' : ''}`} alt={""}/>
                    </>
                    :
                    isDragActive ?
                        <img src={InputIconUpload} className={"animate-bounce"} alt={"Drop your file here"} /> :
                    <img src={InputIconUpload} alt={"Drag your file here to upload"}/>
                }
            </div>

        </div>
    );
}
